import type { RefObject } from 'react'
import { useEffect, useState } from 'react'

interface UseContainerScrollBreakpointConfig {
  breakpoint?: number
  /** If true, the scroll breakpoint will be reversed.
   * E.g If breakpoint is 10px, true will be returned when scrollbar is 10px or more before the end of the container. */
  reverse?: boolean
  containerElement: RefObject<HTMLElement | null>
  /** If true, the scroll breakpoint will be checked for the vertical scrollbar. Otherwise, the horizontal scrollbar will be checked. */
  vertical?: boolean
}

const getNewScrollBreakpoint = (
  reverse: boolean,
  breakpoint: number,
  containerElement: HTMLElement | null,
  vertical?: boolean,
) => {
  if (!containerElement) return false
  if (vertical) {
    if (reverse) {
      const maxScroll = containerElement.scrollHeight - containerElement.clientHeight
      return maxScroll - containerElement.scrollTop < breakpoint
    }
    return containerElement.scrollTop > breakpoint
  } else {
    if (reverse) {
      const maxScroll = containerElement.scrollWidth - containerElement.clientWidth
      return maxScroll - containerElement.scrollLeft < breakpoint
    }
    return containerElement.scrollLeft > breakpoint
  }
}

/** Returns true if the window has been scrolled past the breakpoint. Default breakpoint is 10px. */
export function useContainerScrollBreakpoint({
  breakpoint = 10,
  reverse = false,
  containerElement,
  vertical = true,
}: UseContainerScrollBreakpointConfig) {
  const initialScrollBreakpoint = getNewScrollBreakpoint(reverse, breakpoint, containerElement?.current, vertical)
  const [scrollBreakpoint, setScrollBreakpoint] = useState(initialScrollBreakpoint)

  useEffect(() => {
    const updateBreakpoint = () => {
      setScrollBreakpoint(getNewScrollBreakpoint(reverse, breakpoint, containerElement?.current, vertical))
    }
    // Update the breakpoint when the window is scrolled or resized
    containerElement?.current?.addEventListener('scroll', updateBreakpoint)
    window.addEventListener('resize', updateBreakpoint)
    // Update the breakpoint immediately
    updateBreakpoint()
    return () => {
      containerElement?.current?.removeEventListener('scroll', updateBreakpoint)
      window.removeEventListener('resize', updateBreakpoint)
    }
  }, [setScrollBreakpoint, reverse, breakpoint, containerElement?.current, vertical])

  return scrollBreakpoint
}
